import { useState } from 'react';
import {
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
  Select,
} from '@/components/ui';
import { useBillActions, useFeeServices } from '@/features/billing/api';
import { formatRM, parseRM } from '@/features/billing/money';
import type { Bill } from '@/features/billing/types';
import { useLabCatalogue } from '@/features/orders/api';
import styles from '@/pages/queue/queue.module.css';

type Source = 'service' | 'test' | 'other';

/** "Add bill line" (artifact #32): from the services list, and who pays it. */
export function AddLineDialog({
  bill: b,
  onClose,
  onError,
}: {
  bill: Bill;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const a = useBillActions(b.id);
  const { data: services } = useFeeServices();
  const { data: tests } = useLabCatalogue();
  const [source, setSource] = useState<Source>('service');
  const [serviceId, setServiceId] = useState('');
  const [testId, setTestId] = useState('');
  const [desc, setDesc] = useState('');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('1');
  const [covered, setCovered] = useState(!!b.payer);
  const priceSen = parseRM(price);
  const qtyN = Number(qty);
  const here = (services ?? []).filter(
    (s) => s.allBranches || s.branches.some((x) => x.id === b.branch.id),
  );
  const ok =
    qtyN >= 1 &&
    (source === 'service'
      ? !!serviceId
      : source === 'test'
        ? !!testId
        : desc.trim().length >= 2 && priceSen !== null);
  const price$ = (sen: number | null, member: number | null) =>
    sen === null
      ? 'price not set'
      : b.memberPrice && member !== null
        ? `${formatRM(member)} member`
        : formatRM(sen);

  return (
    <Dialog
      open
      variant="modal"
      title="Add bill line"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ok}
            loading={a.addLine.isPending}
            onClick={() =>
              a.addLine
                .mutateAsync({
                  kind: source === 'service' ? 'service' : 'other',
                  serviceId: source === 'service' ? serviceId : undefined,
                  labTestId: source === 'test' ? testId : undefined,
                  description: source === 'other' ? desc.trim() : undefined,
                  unitPriceSen:
                    source === 'other' ? (priceSen ?? undefined) : undefined,
                  quantity: qtyN,
                  covered: b.payer ? covered : undefined,
                })
                .then(onClose)
                .catch(onError)
            }
          >
            Add line
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <ChipToggle
          label="From"
          single
          options={[
            { value: 'service', label: 'Services list' },
            { value: 'test', label: 'Lab test' },
            { value: 'other', label: 'Other item' },
          ]}
          selected={[source]}
          onChange={(v) => v[0] && setSource(v[0])}
        />
        {source === 'service' && (
          <Field label="Service">
            <Select
              value={serviceId}
              onChange={(ev) => setServiceId(ev.target.value)}
              options={[
                { value: '', label: 'Choose…' },
                ...here.map((s) => ({
                  value: s.id,
                  label: `${s.name} · ${price$(s.priceSen, s.memberPriceSen)}`,
                })),
              ]}
            />
          </Field>
        )}
        {source === 'test' && (
          <Field label="Lab test">
            <Select
              value={testId}
              onChange={(ev) => setTestId(ev.target.value)}
              options={[
                { value: '', label: 'Choose…' },
                ...(tests ?? []).map((t) => ({
                  value: t.id,
                  label: `${t.name} · ${price$(t.priceSen, t.memberPriceSen)}`,
                })),
              ]}
            />
          </Field>
        )}
        {source === 'other' && (
          <>
            <Field label="Item">
              <Input
                placeholder="Dressing pack"
                value={desc}
                onChange={(ev) => setDesc(ev.target.value)}
              />
            </Field>
            <Field label="Unit price (RM)">
              <Input
                inputMode="decimal"
                value={price}
                onChange={(ev) => setPrice(ev.target.value)}
              />
            </Field>
          </>
        )}
        <Field label="Quantity">
          <Input
            inputMode="numeric"
            value={qty}
            onChange={(ev) => setQty(ev.target.value.replace(/\D/g, ''))}
          />
        </Field>
        {b.payer && (
          <ChipToggle
            label={`Does ${b.payer.name} cover it?`}
            single
            options={[
              { value: 'yes', label: `Covered by ${b.payer.name}` },
              { value: 'no', label: 'Excluded · patient pays' },
            ]}
            selected={[covered ? 'yes' : 'no']}
            onChange={(v) => v[0] && setCovered(v[0] === 'yes')}
          />
        )}
      </div>
    </Dialog>
  );
}
